import React from "react";
import { Link } from "react-router-dom";
import { Phone, MessageCircle, Facebook, MapPin, Mail, Heart, Star } from "lucide-react";
import { SITE, waLink } from "../lib/site";

export default function Footer() {
  return (
    <footer data-testid="site-footer" className="relative mt-32 pt-20 pb-10 bg-gradient-to-b from-brand-snow via-brand-cloud to-brand-silver/60 overflow-hidden">
      <div
        className="absolute -top-32 left-1/2 -translate-x-1/2 w-[900px] h-[900px] rounded-full opacity-20 pointer-events-none"
        style={{ background: "radial-gradient(circle, #4FA3FF 0%, transparent 60%)" }}
      />
      <div className="relative mx-auto max-w-7xl px-6 md:px-12 grid lg:grid-cols-4 md:grid-cols-2 gap-12">
        <div className="lg:col-span-2 max-w-md">
          <div className="flex items-center gap-3 mb-5">
            <img src={SITE.logo} alt="Dr. Jairo Franco" className="w-12 h-12 rounded-full ring-1 ring-brand-silver" />
            <div>
              <p className="font-serif text-xl text-brand-deep leading-tight">Dr. Jairo Franco</p>
              <p className="text-xs uppercase tracking-[0.2em] text-brand-medical">Ginecólogo · Obstetra</p>
            </div>
          </div>
          <p className="text-brand-deep/70 font-light leading-relaxed">
            Atención ginecológica y obstétrica con calidez humana, experiencia clínica
            y tecnología médica al servicio de tu bienestar.
          </p>
          <p className="mt-5 text-xs uppercase tracking-[0.2em] text-brand-medical/80">
            Céd. Especialidad <span className="text-brand-deep font-medium">{SITE.cedulaEspecialidad}</span>
          </p>
        </div>

        <div>
          <p className="font-serif text-lg text-brand-deep mb-4">Navegación</p>
          <ul className="space-y-2.5 text-sm">
            <li><Link to="/" className="text-brand-deep/70 hover:text-brand-medical transition-colors">Inicio</Link></li>
            <li><Link to="/quien-soy" className="text-brand-deep/70 hover:text-brand-medical transition-colors">Quién Soy</Link></li>
            <li><Link to="/control-prenatal" className="text-brand-deep/70 hover:text-brand-medical transition-colors">Control Prenatal</Link></li>
            <li><Link to="/salud-ginecologica" className="text-brand-deep/70 hover:text-brand-medical transition-colors">Salud Ginecológica</Link></li>
            <li><Link to="/agendar" className="text-brand-deep/70 hover:text-brand-medical transition-colors">Agendar Cita</Link></li>
          </ul>
        </div>

        <div>
          <p className="font-serif text-lg text-brand-deep mb-4">Contacto</p>
          <ul className="space-y-3 text-sm">
            <li>
              <a href={SITE.bookingUrl} target="_blank" rel="noopener noreferrer" data-testid="footer-whatsapp" className="flex items-center gap-2 text-brand-deep/80 hover:text-brand-medical transition-colors">
                <MessageCircle className="w-4 h-4" strokeWidth={1.4} /> {SITE.whatsappDisplay}
              </a>
            </li>
            <li>
              <a href={`tel:+${SITE.whatsappRaw}`} className="flex items-center gap-2 text-brand-deep/80 hover:text-brand-medical transition-colors">
                <Phone className="w-4 h-4" strokeWidth={1.4} /> Llamar
              </a>
            </li>
            <li>
              <a href={SITE.facebook} target="_blank" rel="noopener noreferrer" data-testid="footer-facebook" className="flex items-center gap-2 text-brand-deep/80 hover:text-brand-medical transition-colors">
                <Facebook className="w-4 h-4" strokeWidth={1.4} /> Facebook
              </a>
            </li>
            <li>
              <a href={SITE.googleReviews} target="_blank" rel="noopener noreferrer" data-testid="footer-google-reviews" className="flex items-center gap-2 text-brand-deep/80 hover:text-brand-medical transition-colors">
                <Star className="w-4 h-4 fill-brand-bright text-brand-bright" strokeWidth={1.4} /> Opiniones en Google
              </a>
            </li>
            <li>
              <p className="flex items-center gap-2 text-xs uppercase tracking-[0.18em] text-brand-medical mb-2">
                <MapPin className="w-3.5 h-3.5" strokeWidth={1.5} /> Consultorios
              </p>
              <div className="space-y-2.5">
                {SITE.addresses.map((a, i) => (
                  <a
                    key={i}
                    href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(a.mapsQuery)}`}
                    target="_blank" rel="noopener noreferrer"
                    data-testid={`footer-address-${i}`}
                    className="block text-brand-deep/80 hover:text-brand-medical transition-colors leading-snug"
                  >
                    <span className="font-medium text-brand-deep block">{a.place}</span>
                    <span className="text-brand-deep/60 text-xs">
                      {a.street && `${a.street}, `}{a.neighborhood}, {a.city}
                    </span>
                  </a>
                ))}
              </div>
            </li>
          </ul>
        </div>
      </div>

      <div className="relative mx-auto max-w-7xl px-6 md:px-12 mt-16 pt-6 border-t border-brand-silver/60 flex flex-col md:flex-row items-center justify-between gap-3 text-xs text-brand-deep/60">
        <p>© {new Date().getFullYear()} Dr. Jairo Franco. Todos los derechos reservados.</p>
        <p className="flex items-center gap-3">
          <span className="flex items-center gap-1.5">
            Cuidando la salud femenina <Heart className="w-3.5 h-3.5 text-brand-bright" strokeWidth={1.6} />
          </span>
          <span className="text-brand-deep/30">·</span>
          <Link to="/admin/login" data-testid="footer-admin-link" className="text-brand-deep/40 hover:text-brand-medical transition-colors">
            Acceso admin
          </Link>
        </p>
      </div>
    </footer>
  );
}
